import { AccordionButton, AccordionIcon, AccordionItem, Box, Flex, Grid, Tag, Tooltip, Text } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useDeleteGroupAutomationMutation, useUpdateGroupAutomationMutation } from '../api/automationsApi'
import { toast } from 'react-toastify'
import { EditIcon } from '@chakra-ui/icons'
import { DeleteModal } from '../../../utils/DeleteModal'
import { formatAlarmDate, uniqueTitlesReducer } from '../../../utils/helpers'
import RegisterAutomationModal from './RegisterAutomation'
import { AccordionPanelAutomations } from './AccordionPanelAutomations'

type Props = {
    index: number
    automation: any
}
export const GroupCustomAccordionItem = ({
    index,
    automation,
}: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [deleteGroupAutomation] = useDeleteGroupAutomationMutation()
    const [updateGroupAutomation] = useUpdateGroupAutomationMutation()

    const handleEnableDisable = async (e) => {
        e.stopPropagation()
        const enableDisable = !automation.is_enabled ? 'enabled' : 'disabled'

        try {
            await updateGroupAutomation({ id: automation.id, body: { is_enabled: !automation.is_enabled } }).unwrap()
            toast.success(`Automation ${enableDisable}`, {
                style: { padding: '10px 20px' },
                isLoading: false,
                autoClose: 3000,
            })
        } catch (error) {
            toast.error(`Failed to ${enableDisable} automation`, {
                style: { padding: '10px 20px' },
                isLoading: false,
                autoClose: 3000,
            })
        }
    }
    const locationsTitles = uniqueTitlesReducer(automation?.devices?.map((device) => device.location.title))
    const group = uniqueTitlesReducer(automation?.devices?.map((device) => device.group.name))
    return (
        <AccordionItem
            outline="none"
            boxShadow="none"
        >
            {({ isExpanded }) => (
                <>
                    <AccordionButton
                        outline="none"
                        boxShadow="none"
                        px="0"
                        sx={{
                            outline: 'none',
                            _hover: { bg: isExpanded ? 'none' : 'gray.100' },
                            _focus: { boxShadow: 'none' },
                            transitionDuration: '0.3s',
                        }}
                    >
                        <Grid
                            templateColumns="150px 0.7fr 0.7fr 1fr 1fr 1fr 1fr"
                            alignItems="center"
                            gap={6}
                            p={4}
                            key={index}
                            w="full"
                        >
                            <Flex align="center">
                                <AccordionIcon mr="4" />
                                <Flex direction="column" textAlign="left">
                                    <Tooltip label={automation?.name} hasArrow placement="top">
                                        <Text
                                            fontSize="14px"
                                            fontWeight="700"
                                            color="text.dark"
                                            textAlign="left"
                                            whiteSpace="nowrap"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                            maxWidth="150px"
                                        >
                                            {automation?.name?.length > 14 ? `${automation.name.slice(0, 14)}...` : automation?.name}
                                        </Text>
                                    </Tooltip>
                                </Flex>
                            </Flex>
                            <Flex align="center">
                                <Tag variant="subtle" colorScheme="cyan">
                                    {automation?.type === "time" ? "Time" : "Device"}
                                </Tag>
                            </Flex>
                            <Flex direction="column" textAlign="left">
                                <Tooltip
                                    label={locationsTitles.join(', ')}
                                    hasArrow
                                    placement="top"
                                >
                                    <Text
                                        fontSize="14px"
                                        fontWeight="700"
                                        color="text.dark"
                                        textAlign={locationsTitles.length > 0 ? "left" : "center"}
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        maxWidth="150px"
                                    >
                                        {locationsTitles.length > 0
                                            ? `${locationsTitles[0]}${locationsTitles.length > 1 ? ', ' + locationsTitles[1] + '...' : ''
                                            }`
                                            : '-'}
                                    </Text>
                                </Tooltip>

                            </Flex>
                            <Flex align="center">
                                <Tooltip
                                    label={group.join(', ')}
                                    hasArrow
                                    placement="top"
                                >
                                    <Text
                                        fontSize="14px"
                                        fontWeight="700"
                                        color="text.dark"
                                        textAlign="left"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        maxWidth="150px"
                                    >
                                        {group.length > 0 ? group[0] : "-" || "-"}
                                    </Text>
                                </Tooltip>

                            </Flex>
                            <Flex align="center">
                                <Tooltip
                                    label={automation?.devices?.map((device) => device.title).join(', ')}
                                    hasArrow
                                    placement="top"
                                >
                                    <Text
                                        fontSize="14px"
                                        fontWeight="700"
                                        color="text.dark"
                                        textAlign="left"
                                        whiteSpace="nowrap"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        maxWidth="150px"
                                    >
                                        {automation?.devices?.length > 0
                                            ? `${automation?.devices[0].title}${automation?.devices.length > 1 ? ', ' + automation?.devices[1].title + '...' : ''
                                            }`
                                            : '-'}
                                    </Text>
                                </Tooltip>
                            </Flex>
                            <Flex align="center">
                                <Text
                                    fontSize="12px"
                                    fontWeight="400"
                                    color="text.light"
                                    textAlign="left"
                                >
                                    {formatAlarmDate(automation?.created_at)}
                                </Text>
                            </Flex>
                            <Flex gap="2">
                                <Flex gap="2" alignItems="center">
                                    <Tooltip
                                        hasArrow
                                        rounded="lg"
                                        label={
                                            <Text pl="10px" p="6px">
                                                {automation?.is_enabled ? 'Disable' : 'Enable'}
                                            </Text>
                                        }
                                        placement="top"
                                        bg="heading.dark"
                                    >
                                        <Flex
                                            as="button"
                                            onClick={handleEnableDisable}
                                            w="50px"
                                            h="28px"
                                            bg={automation?.is_enabled ? 'green.400' : 'gray.600'}
                                            rounded="full"
                                            p="4px"
                                            align="center"
                                            transition="background 0.3s ease"
                                            position="relative"
                                        >
                                            <Box
                                                w="20px"
                                                h="20px"
                                                bg="white"
                                                rounded="full"
                                                transition="transform 0.3s ease"
                                                transform={
                                                    automation?.is_enabled ? 'translateX(22px)' : 'translateX(0)'
                                                }
                                                shadow="md"
                                            />
                                        </Flex>
                                    </Tooltip>
                                    <Tooltip
                                        hasArrow
                                        rounded="lg"
                                        label={
                                            <Text pl="10px" p="6px">
                                                Edit
                                            </Text>
                                        }
                                        placement="top"
                                        bg="heading.dark"
                                    >
                                        <Flex
                                            color="icon.strong"
                                            bg="#F7F9FD"
                                            rounded="lg"
                                            align="center"
                                            justifyContent="space-around"
                                            boxSize="32px"
                                            _hover={{ bg: '#E8EEFD' }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setIsModalOpen(true)
                                            }}
                                        >
                                            <EditIcon />
                                        </Flex>
                                    </Tooltip>
                                    <DeleteModal
                                        recordId={automation.id}
                                        mutationFn={deleteGroupAutomation}
                                    />
                                </Flex>
                            </Flex>
                        </Grid>
                    </AccordionButton>
                    <RegisterAutomationModal
                        isOpen={isModalOpen}
                        automationToEdit={automation}
                        automationType="group"
                        updateAutomation={updateGroupAutomation}
                        handleClose={() => {
                            setIsModalOpen(false)
                        }}
                    />
                    <AccordionPanelAutomations automation={automation} />
                </>
            )}
        </AccordionItem>
    )
}
